import { gsap } from 'gsap';
import type { PresentationScene, SceneMeta, SceneScript } from './types';
import type { PresentationContext, ResolvedScene } from '../presentation/types';
import type { ThreeStage } from '../three/ThreeStage';

/** Users who ask for reduced motion get every step as an instant, settled state (05-SCENES §Controls). */
export const prefersReducedMotion = (): boolean => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export abstract class BaseScene implements PresentationScene {
  public readonly root: HTMLElement;
  public step = 0;

  public readonly meta: SceneMeta;
  public readonly script: SceneScript;
  /** Which modules and products are enabled for this run. */
  protected readonly context: PresentationContext;
  public abstract readonly stepCount: number;

  private stepTimeline?: gsap.core.Timeline;
  private loops: gsap.core.Animation[] = [];

  constructor(resolved: ResolvedScene) {
    this.meta = resolved.meta;
    this.script = resolved.script;
    this.context = resolved.context;
    this.root = document.createElement('section');
    this.root.className = 'presentation-scene';
  }

  public mount(host: HTMLElement): void {
    if (import.meta.env.DEV && this.script.presenterNotes.length !== this.stepCount) {
      console.error(`${this.meta.id}: ${this.stepCount} steps but ${this.script.presenterNotes.length} presenter notes`);
    }
    this.root.dataset.scene = this.meta.id;
    this.root.dataset.brand = this.meta.brand;
    this.root.dataset.tone = this.meta.tone;
    this.root.dataset.step = '0';
    this.root.setAttribute('aria-label', this.meta.title);
    this.root.innerHTML = this.template();
    host.appendChild(this.root);
  }

  /**
   * enter/exit only own the scene container. Step state is prepared by goToStep().
   * Keeping those responsibilities separate prevents a scene from being initialised twice
   * during forward/backward navigation.
   */
  public enter(_stage: ThreeStage, immediate = false): void {
    immediate = immediate || prefersReducedMotion();
    this.root.classList.add('is-active');
    this.root.style.pointerEvents = 'auto';
    document.documentElement.style.setProperty('--accent', this.meta.accent);

    gsap.killTweensOf(this.root);
    if (immediate) {
      gsap.set(this.root, { autoAlpha: 1 });
    } else {
      gsap.fromTo(
        this.root,
        { autoAlpha: 0 },
        { autoAlpha: 1, duration: 0.38, ease: 'power2.out', overwrite: true },
      );
    }
    this.onEnter(immediate);
  }

  public exit(_stage: ThreeStage): void {
    this.root.classList.remove('is-active');
    this.root.style.pointerEvents = 'none';

    // Stop delayed/staggered scene-local animations so they cannot mutate hidden scenes.
    gsap.killTweensOf(this.root);
    gsap.killTweensOf(this.root.querySelectorAll('*'));
    this.stepTimeline?.kill();
    this.stepTimeline = undefined;
    this.stopLoops();
    this.onExit();
    gsap.set(this.root, { autoAlpha: 0 });
  }

  public next(stage: ThreeStage): boolean {
    if (this.step >= this.stepCount - 1) return false;
    this.goToStep(this.step + 1, stage);
    return true;
  }

  public previous(stage: ThreeStage): boolean {
    if (this.step <= 0) return false;
    this.goToStep(this.step - 1, stage);
    return true;
  }

  public goToStep(step: number, stage: ThreeStage, immediate = false): void {
    immediate = immediate || prefersReducedMotion();
    const previousStep = this.step;
    this.step = Math.max(0, Math.min(step, this.stepCount - 1));
    this.root.dataset.step = String(this.step);

    this.applyFragments(immediate);
    this.onStep(this.step, stage, immediate, previousStep);
  }

  public reset(stage: ThreeStage): void {
    this.goToStep(0, stage, true);
  }

  /**
   * Fragments have exactly one owner: BaseScene.
   * Scene-specific animations must target children inside a fragment, not the fragment
   * element's opacity/visibility. This removes the old autoAlpha/opacity race that caused
   * content to be invisible until the user navigated away and back.
   */
  protected applyFragments(immediate = false): void {
    const fragments = Array.from(this.root.querySelectorAll<HTMLElement>('[data-from]'));

    fragments.forEach((el) => {
      const from = Number(el.dataset.from ?? 0);
      const until = el.dataset.until ? Number(el.dataset.until) : Number.POSITIVE_INFINITY;
      const visible = this.step >= from && this.step <= until;
      const wasVisible = el.dataset.fragmentVisible === 'true';

      el.dataset.fragmentVisible = String(visible);
      el.setAttribute('aria-hidden', String(!visible));
      gsap.killTweensOf(el);

      if (immediate) {
        gsap.set(el, {
          opacity: visible ? 1 : 0,
          visibility: visible ? 'visible' : 'hidden',
        });
        return;
      }

      // No state change: enforce the deterministic end state without replaying animation.
      if (visible === wasVisible) {
        gsap.set(el, {
          opacity: visible ? 1 : 0,
          visibility: visible ? 'visible' : 'hidden',
        });
        return;
      }

      if (visible) {
        gsap.set(el, { visibility: 'visible' });
        gsap.fromTo(
          el,
          { opacity: 0 },
          {
            opacity: 1,
            duration: 0.44,
            ease: 'power3.out',
            overwrite: true,
          },
        );
      } else {
        gsap.to(el, {
          opacity: 0,
          duration: 0.2,
          ease: 'power2.in',
          overwrite: true,
          onComplete: () => {
            // Only hide if this fragment is still meant to be hidden. A fast reverse input
            // may have changed the state while this tween was running.
            if (el.dataset.fragmentVisible !== 'true') el.style.visibility = 'hidden';
          },
        });
      }
    });
  }

  /** One timeline per step change. Starting a new one kills the previous so fast input never stacks motion. */
  protected timeline(): gsap.core.Timeline {
    this.stepTimeline?.kill();
    this.stepTimeline = gsap.timeline();
    return this.stepTimeline;
  }

  /** Register an ambient/repeating animation. All loops are killed on exit or via stopLoops(). */
  protected loop<T extends gsap.core.Animation>(animation: T): T {
    this.loops.push(animation);
    // Ambient loops are decoration: with reduced motion they hold still.
    if (prefersReducedMotion()) animation.pause();
    return animation;
  }

  protected get hasLoops(): boolean {
    return this.loops.length > 0;
  }

  protected stopLoops(): void {
    this.loops.forEach((animation) => animation.kill());
    this.loops = [];
  }

  protected $<T extends Element = HTMLElement>(selector: string): T {
    const element = this.root.querySelector<T>(selector);
    if (!element) throw new Error(`${this.meta.id}: missing ${selector}`);
    return element;
  }

  protected $$<T extends Element = HTMLElement>(selector: string): T[] {
    return Array.from(this.root.querySelectorAll<T>(selector));
  }

  /**
   * Called after the scene becomes active. Scene switches always prepare the target step
   * instantly, so this is where a scene plays its step-0 entrance choreography.
   */
  protected onEnter(immediate: boolean): void {}

  /** Hook for scene-local cleanup that is not a GSAP tween (classes, flags). */
  protected onExit(): void {}

  protected abstract template(): string;
  protected abstract onStep(step: number, stage: ThreeStage, immediate: boolean, previousStep: number): void;
}
